import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { onPushMessage } from '../lib/push';
import { translateNotification } from '../lib/server-text';
import { useApp } from '../lib/app-context';
import { Avatar, Badge } from './ui';
import LanguageSwitcher from './LanguageSwitcher';
import PushPrompt from './PushPrompt';

const PUSH_PAGES = ['/compte', '/hote', '/messages', '/notifications', '/reservations'];
import {
  BellIcon,
  ChatIcon,
  CloseIcon,
  InstagramIcon,
  MenuIcon,
  MoonIcon,
  LoginIcon,
  PeopleIcon,
  PlusIcon,
  SearchIcon,
  SunIcon,
  UserIcon,
} from './icons';
import { t } from '../lib/i18n';

/**
 * The shell: header, navigation, toasts, footer.
 *
 * The unread badges poll rather than subscribe. The WebSocket exists and the
 * messaging page uses it, but a header that depends on a live socket shows the
 * wrong number whenever the socket is blocked — and some Moroccan ISP proxies do
 * block them. A 30-second poll is boring and always right; a push relayed by
 * the service worker, or coming back to the tab, counts again at once.
 */

function useUnreadCounts() {
  const { user, toast } = useApp();
  const location = useLocation();
  const [messages, setMessages] = useState(0);
  const [notifications, setNotifications] = useState(0);
  // The last count seen, so a rise can be announced; null until the first load.
  const seen = useRef<number | null>(null);
  const path = useRef(location.pathname);
  path.current = location.pathname;

  useEffect(() => {
    if (!user) {
      setMessages(0);
      setNotifications(0);
      seen.current = null;
      return;
    }
    let active = true;
    const load = async () => {
      try {
        const [unreadMessages, unreadNotifications] = await Promise.all([
          api.get<{ unread: number }>('/messages/unread-count'),
          api.get<{ unread: number; items: { title: string; body: string }[] }>('/notifications?limit=1'),
        ]);
        if (!active) return;
        setMessages(unreadMessages.unread);
        setNotifications(unreadNotifications.unread);

        /*
         * Something new while the page is open: say what, in a toast — for
         * everyone, including those who never allowed push. Not on the
         * notifications page itself, which already shows it.
         */
        const previous = seen.current;
        seen.current = unreadNotifications.unread;
        const latest = unreadNotifications.items[0];
        if (previous !== null && unreadNotifications.unread > previous && latest && path.current !== '/notifications') {
          const body = latest.body ? ` — ${translateNotification(latest.body)}` : '';
          toast(`${translateNotification(latest.title)}${body}`, 'info');
        }
      } catch {
        // A failed count is not worth a toast.
      }
    };
    void load();
    // A push relayed by the service worker: count again now, not in 30 seconds.
    const stopListening = onPushMessage(() => void load());
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, 30_000);
    return () => {
      active = false;
      clearInterval(timer);
      stopListening();
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { messages, notifications };
}

export default function Layout() {
  const { user, isHost, isAdmin, theme, toggleTheme, toasts, signOut } = useApp();
  const navigate = useNavigate();
  const unread = useUnreadCounts();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // A menu that stays open over the page you just navigated to is a menu you
  // have to close twice.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  /*
   * Five at most — the most a phone's width holds with labels still legible.
   * Supply matters as much as demand, so "Publier" earns a slot of its own;
   * hosts get their dashboard there instead.
   */
  const tabs = [
    { to: '/recherche', label: t("Explorer"), icon: <SearchIcon /> },
    { to: '/colocations', label: t("Colocations"), icon: <PeopleIcon /> },
    isHost
      ? { to: '/hote', label: t("Hôte"), icon: <PlusIcon /> }
      : { to: '/devenir-hote', label: t("Publier"), icon: <PlusIcon /> },
    ...(user
      ? [
          { to: '/messages', label: t("Messages"), icon: <ChatIcon />, badge: unread.messages },
          { to: '/compte', label: t("Compte"), icon: <UserIcon /> },
        ]
      : [{ to: '/connexion', label: t("Connexion"), icon: <LoginIcon /> }]),
  ];

  const links = [
    { to: '/recherche', label: t("Explorer") },
    { to: '/colocations', label: t("Colocations") },
    ...(user ? [{ to: '/messages', label: t("Messages") }] : []),
    ...(user ? [{ to: '/compte', label: t("Mon compte") }] : []),
    ...(isHost ? [{ to: '/hote', label: t("Mode hôte") }] : []),
    ...(isAdmin ? [{ to: '/admin', label: t("Admin") }] : []),
  ];

  return (
    <>
      <header className="header">
        <Link to="/" className="brand" aria-label={t("Bedly, accueil")}>
          {/* The mark is decorative: the wordmark beside it already names the brand,
              and the link carries its own label. */}
          <span className="brand__mark" aria-hidden />
          <span>{t("Bedly")}</span>
        </Link>

        <nav className="nav" aria-label={t("Navigation principale")}>
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) => `nav__link ${isActive ? 'nav__link--active' : ''}`}
            >
              {link.label}
              {link.to === '/messages' && unread.messages > 0 ? (
                <span style={{ marginInlineStart: 6 }}>
                  <Badge tone="badge--verified">{unread.messages}</Badge>
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <span className="spacer" />

        <LanguageSwitcher />
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? t("Passer en thème clair") : t("Passer en thème sombre")}
          title={theme === 'dark' ? t("Thème clair") : t("Thème sombre")}
        >
          {/* An icon, not a ☾ glyph: the glyph was a few pixels wide and read as
              decoration on a phone. */}
          {theme === 'dark' ? <SunIcon className="icon-22" /> : <MoonIcon className="icon-22" />}
        </button>

        {user ? (
          <>
            <Link to="/notifications" className="btn btn--quiet btn--sm" aria-label={t("Notifications")}>
              <BellIcon className="icon-18" />
              {unread.notifications > 0 ? (
                <span style={{ marginInlineStart: 4 }}>{unread.notifications}</span>
              ) : null}
            </Link>
            {isHost ? (
              <Link to="/hote/annonces/nouvelle" className="btn btn--sm header__desktop-only">
                {t("Publier une annonce")}
              </Link>
            ) : (
              <Link to="/devenir-hote" className="btn btn--sm header__desktop-only">
                {t("Devenir hôte")}
              </Link>
            )}
            <button
              type="button"
              className="btn btn--quiet btn--sm header__desktop-only"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-label={t("Menu du compte")}
            >
              <Avatar name={user.fullName} url={user.avatarUrl} size={28} />
            </button>
          </>
        ) : (
          <>
            {/* On a phone "Connexion" is in the tab bar; the header keeps the one
                action that matters most for a visitor. */}
            <Link to="/connexion" className="btn btn--ghost btn--sm header__desktop-only">
              {t("Se connecter")}
            </Link>
            <Link to="/inscription" className="btn btn--sm">
              <span className="only-desktop">{t("Créer un compte")}</span>
              {/* The short label is what lets the menu button fit beside it at 375px. */}
              <span className="only-mobile">{t("S’inscrire")}</span>
            </Link>
          </>
        )}

        <button
          type="button"
          className="btn btn--quiet btn--sm header__mobile-only"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="main-menu"
          aria-label={menuOpen ? t("Fermer le menu") : t("Ouvrir le menu")}
        >
          {menuOpen ? <CloseIcon className="icon-22" /> : <MenuIcon className="icon-22" />}
        </button>
      </header>

      {menuOpen ? (
        <>
          <button
            type="button"
            className="menu-backdrop"
            aria-label={t("Fermer le menu")}
            onClick={() => setMenuOpen(false)}
          />
          <nav id="main-menu" className="menu-panel" aria-label={t("Menu")}>
            {user ? (
              <div className="menu-panel__who header__mobile-only">
                <Avatar name={user.fullName} url={user.avatarUrl} size={36} />
                <div className="stack" style={{ gap: 0 }}>
                  <strong>{user.fullName}</strong>
                  <span className="tiny muted">{user.email}</span>
                </div>
              </div>
            ) : null}

            {(user
              ? [
                  ...links,
                  { to: '/parametres', label: t("Paramètres") },
                  { to: '/verification', label: t("Vérification d’identité") },
                ]
              : [
                  { to: '/recherche', label: t("Explorer les logements") },
                  { to: '/colocations', label: t("Colocations") },
                  { to: '/devenir-hote', label: t("Publier un logement") },
                  { to: '/connexion', label: t("Se connecter") },
                  { to: '/inscription', label: t("Créer un compte") },
                ]
            ).map((link) => (
              <Link key={link.to} to={link.to} className="menu-panel__item">
                {link.label}
              </Link>
            ))}

            {user ? (
              <button
                type="button"
                className="menu-panel__item menu-panel__item--danger"
                onClick={async () => {
                  setMenuOpen(false);
                  await signOut();
                  navigate('/');
                }}
              >
                {t("Se déconnecter")}
              </button>
            ) : null}
          </nav>
        </>
      ) : null}

      <main>
        {/* Where people wait on someone else, invite them to be told on their phone. */}
        {user && PUSH_PAGES.some((prefix) => location.pathname.startsWith(prefix)) ? (
          <div className="push-prompt-slot">
            <PushPrompt variant="banner" />
          </div>
        ) : null}
        <Outlet />
      </main>

      <footer
        style={{
          borderTop: '1px solid var(--line)',
          padding: '26px clamp(16px, 4vw, 40px)',
          color: 'var(--ink2)',
          fontSize: 13,
        }}
      >
        <div className="row row--wrap" style={{ gap: 16, maxWidth: 1180, margin: '0 auto' }}>
          <span className="strong" style={{ color: 'var(--ink)' }}>
            {t("Bedly")}
          </span>
          <span>{t("Colocations, chambres au mois et locations au Maroc.")}</span>
          <span className="spacer" />
          <span>{t("Montants en dirhams (MAD), taxes incluses.")}</span>
          <a
            className="btn btn--ghost btn--sm"
            href="https://www.instagram.com/bedly.ma/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("Bedly sur Instagram (@bedly.ma)")}
          >
            <InstagramIcon className="icon-18" />
            {t("@bedly.ma")}
          </a>
        </div>
      </footer>

      <nav className="tabbar" aria-label={t("Navigation")}>
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) => `tabbar__link ${isActive ? 'tabbar__link--active' : ''}`}
          >
            {tab.icon}
            <span>{tab.label}</span>
            {'badge' in tab && tab.badge ? (
              <span className="tabbar__badge" aria-label={t('{count} non lus', { count: tab.badge })}>
                {tab.badge > 99 ? '99+' : tab.badge}
              </span>
            ) : null}
          </NavLink>
        ))}
      </nav>

      <div className="toasts" aria-live="polite">
        {toasts.map((item) => (
          <div
            key={item.id}
            className={`toast ${item.tone === 'error' ? 'toast--error' : item.tone === 'ok' ? 'toast--ok' : ''}`}
          >
            {item.message}
          </div>
        ))}
      </div>
    </>
  );
}
